import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicDocumentApi, type DirectCreditMemoResponse, type PublicBusinessContactInfo } from '@/lib/api';
import { transformLineItems, calculateSubtotal } from '@/components/TemplateDesigner/utils';
import { DocumentRenderer, buildOwlOpsPayload } from '@/components/TemplateDesigner/DocumentRenderer';
import type { TemplateSettings, DocumentData, BusinessData } from '@/components/TemplateDesigner/types';
import { DocumentNotFound } from './DocumentNotFound';
import { FullDocumentView } from './FullDocumentView';
import { Loader2 } from 'lucide-react';

export function PublicCreditMemo() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DirectCreditMemoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFoundBusiness, setNotFoundBusiness] = useState<PublicBusinessContactInfo | null>(null);

  // Prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!id) return;

    const loadCM = async () => {
      const response = await publicDocumentApi.getCreditMemo(id);

      if (response.error) {
        // Try to extract business info from error response for not found page
        try {
          const errorData = JSON.parse(response.error);
          if (errorData.business) {
            setNotFoundBusiness(errorData.business);
          }
        } catch {
          // Ignore parse errors
        }
        setLoading(false);
        return;
      }

      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };

    loadCM();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return <DocumentNotFound documentType="Credit Memo" business={notFoundBusiness} />;
  }

  const { credit_memo, client, business, taxes, template_config } = data;

  // Get template settings from template_config
  const templateSettings = template_config?.settings as unknown as TemplateSettings | undefined;

  // Build document data for renderer (memoized)
  const documentData: DocumentData | null = useMemo(() => {
    // Use computed values (markup baked into line items)
    const computedSubtotal = calculateSubtotal(credit_memo.line_items);

    return {
      documentType: 'credit_memo' as const,
      id: credit_memo.id,
      credit_memo_number: credit_memo.credit_memo_number,
      credit_memo_date: credit_memo.credit_memo_date || '',
      status: credit_memo.status,
      client_name: client?.billing_name || '',
      client_company: client?.company_name || '',
      client_address: client?.address || '',
      note_for_client: credit_memo.note_for_client || '',
      line_items: transformLineItems(credit_memo.line_items, taxes || {}),
      subtotal: computedSubtotal,
      total_tax: parseFloat(credit_memo.total_tax),  // Use backend calculation
      total_amount: parseFloat(credit_memo.total_amount),
      // Custom fields
      custom_field_values: credit_memo.custom_field_values,
    };
  }, [credit_memo, client, taxes]);

  const businessData: BusinessData = useMemo(() => {
    return {
      name: business.name,
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      logo: business.logo || '',
    };
  }, [business]);

  // On-demand PDF generation callback
  const handleDownloadPdf = useCallback(async () => {
    if (!templateSettings || !documentData) {
      throw new Error('Template settings not available');
    }
    const payload = buildOwlOpsPayload(documentData, businessData, templateSettings);
    return publicDocumentApi.renderPdf({
      template_slug: templateSettings.selectedLayout,
      data: payload.data,
      settings: payload.settings,
    });
  }, [documentData, businessData, templateSettings]);

  // Build document content using DocumentRenderer
  const documentContent = templateSettings && documentData ? (
    <DocumentRenderer
      isPublic
      data={documentData}
      settings={templateSettings}
      business={businessData}
    />
  ) : (
    <div className="p-8 text-center text-gray-500">
      Unable to render document. Please try again later.
    </div>
  );

  return (
    <FullDocumentView
      documentType="credit_memo"
      business={business}
      themeColor={templateSettings?.colorTheme?.primary}
      logoUrl={templateSettings?.logo?.url}
      showCompanyName={templateSettings?.logo?.showCompanyName ?? true}
      status={credit_memo.status}
      statusLabel="Awaiting Allocation"
      pdfUrl={credit_memo.pdf_url}
      onDownloadPdf={templateSettings && documentData ? handleDownloadPdf : undefined}
      pdfFilename={`CreditMemo-${credit_memo.credit_memo_number}.pdf`}
    >
      {documentContent}
    </FullDocumentView>
  );
}
